import { ApiError } from '../features/identity/types';

/** Response header the backend echoes on every response (EPN-24A `OperationIdMiddleware`). */
const OPERATION_ID_HEADER = 'X-Operation-Id';

/**
 * Normalizes a non-2xx `Response` into an `ApiError`. The single place that reads `X-Operation-Id` and
 * parses RFC 7807 problem details, so every HTTP path (shared client, identity/profile gateways,
 * multipart uploads) behaves identically.
 *
 * - `operationId` is whatever the surfaced response carried (`Headers.get` is case-insensitive); an
 *   absent or empty header yields `null`. Nothing is ever generated here — the backend is authoritative.
 * - A non-JSON, empty, or malformed body never throws; `detail` is used only when it is a non-empty
 *   string, otherwise `fallbackMessage` is used and the error is flagged as not server-authored.
 * - Response bodies are never logged.
 */
export async function apiErrorFromResponse(response: Response, fallbackMessage: string): Promise<ApiError> {
  const problem: unknown = await response.json().catch(() => null);
  const detail =
    typeof problem === 'object' && problem !== null && 'detail' in problem ? (problem as { detail: unknown }).detail : undefined;
  const hasDetail = typeof detail === 'string' && detail.trim().length > 0;
  const operationId = response.headers.get(OPERATION_ID_HEADER) || null;
  // EPN-105: the stable machine-readable `code` (e.g. SUBSCRIPTION_ACCESS_RESTRICTED) and the parsed problem body,
  // for callers that act on them. Never shown to users.
  const code =
    typeof problem === 'object' && problem !== null && typeof (problem as { code?: unknown }).code === 'string'
      ? (problem as { code: string }).code
      : null;
  const details = typeof problem === 'object' && problem !== null ? (problem as Record<string, unknown>) : null;
  return new ApiError(response.status, hasDetail ? detail : fallbackMessage, operationId, hasDetail, code, details);
}
